import { useState } from 'react';
import clsx from 'clsx';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge';
import { Card, CardHeader } from '../../../components/ui/Card';
import { isConstant, Nil, Table, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { CANDIDATE_STATUSES, type CandidateStatus, type OutreachOverview, type SignalOverviewRow } from '../../../api/outreach';
import {
  CONNECTED_TOOLTIP,
  LAST_ACTIVITY_TOOLTIP,
  CampaignStatus,
  Num,
  Rate,
  campaignBeyondSignal,
  campaignDisplayName,
  formatDateTime,
  formatRate,
  hasLinkedInSuffix,
  isDormantSignal,
  signalTabLabel,
  withoutLinkedInSuffix,
} from './format';
import { plural } from '../../ProjectManagement/formatters';
import { formatNumber } from '../../../utils/numberFormat';

/** Only enrolled (good) and blocked (bad) are statuses; the other stages stay neutral. */
const STATUS_CHIP: Record<CandidateStatus, { label: string; variant: 'default' | 'success' | 'error' }> = {
  proposed: { label: 'proposed', variant: 'default' },
  confirmed: { label: 'confirmed', variant: 'default' },
  enrolled: { label: 'enrolled', variant: 'success' },
  blocked: { label: 'blocked', variant: 'error' },
  dismissed: { label: 'dismissed', variant: 'default' },
};

export function CandidateChips({ counts }: { counts: Record<CandidateStatus, number> }) {
  const present = CANDIDATE_STATUSES.filter((s) => (counts[s] ?? 0) > 0);
  if (present.length === 0) return <span className="text-body-xs text-text-tertiary">none yet</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {present.map((status) => (
        // Badge is whitespace-nowrap, so "1,618 proposed" stays one pill.
        <Badge key={status} variant={STATUS_CHIP[status].variant}>
          <span className="tabular-nums">{formatNumber(counts[status])}</span> {STATUS_CHIP[status].label}
        </Badge>
      ))}
    </div>
  );
}

function SignalCampaignCell({ row, showStatus, stripSuffix }: { row: SignalOverviewRow; showStatus: boolean; stripSuffix: boolean }) {
  if (row.ambiguous.length > 1) {
    return (
      <span className="text-body-xs text-warning-dark" title={row.ambiguous.join('\n')}>
        Ambiguous: {row.ambiguous.length} campaigns match {row.campaignPrefix}
      </span>
    );
  }
  if (!row.campaign) {
    return (
      <span className="text-body-xs text-warning-dark">
        No campaign matches {row.campaignPrefix}
      </span>
    );
  }
  const rest = campaignNameBeyondSignal(row, stripSuffix);
  // A campaign that carries the signal's own name adds nothing: the cell stays empty (no "same name" on every row).
  if (!rest && !showStatus) return null;
  return (
    <div className="flex items-center gap-2 min-w-0">
      {rest && (
        <span className="text-body-xs font-normal text-text-tertiary" title={row.campaign.name}>
          lemlist: {rest}
        </span>
      )}
      {showStatus && <CampaignStatus campaign={row.campaign} />}
    </div>
  );
}

/** The campaign name as drawn, minus whatever the signal column already says; null when it only repeats it. */
function campaignNameBeyondSignal(row: SignalOverviewRow, stripSuffix: boolean): string | null {
  if (!row.campaign) return null;
  const shown = stripSuffix ? withoutLinkedInSuffix(campaignDisplayName(row.campaign)) : campaignDisplayName(row.campaign);
  return campaignBeyondSignal(shown, row.campaignPrefix, row.label);
}

/** The Campaign column only earns its place when some row's campaign says more than its signal. Exported for tests. */
export function campaignColumnNeeded(
  rows: SignalOverviewRow[],
  stripSuffix: boolean,
  showStatus: (row: SignalOverviewRow) => boolean,
): boolean {
  return rows.some(
    (r) => !r.campaign || r.ambiguous.length > 1 || showStatus(r) || campaignNameBeyondSignal(r, stripSuffix) !== null,
  );
}

function statusOf(row: SignalOverviewRow): string {
  return row.campaign ? row.campaign.status ?? 'status unknown' : 'without a campaign';
}

/** Statuses by count, most common first. */
function statusCounts(rows: SignalOverviewRow[]): [string, number][] {
  const byStatus = new Map<string, number>();
  for (const row of rows) byStatus.set(statusOf(row), (byStatus.get(statusOf(row)) ?? 0) + 1);
  return [...byStatus.entries()].sort((a, b) => b[1] - a[1]);
}

/** True when every signal has a campaign and lemlist says each is paused. Exported for tests. */
export function allSignalsPaused(rows: ReadonlyArray<Pick<SignalOverviewRow, 'campaign'>>): boolean {
  return rows.length > 0 && rows.every((r) => r.campaign?.status?.toLowerCase() === 'paused');
}

/** True when every row with a campaign names it "… · LinkedIn": the suffix is then said once, not per row. */
export function allLinkedIn(names: string[]): boolean {
  return names.length > 0 && names.every(hasLinkedInSuffix);
}

/**
 * A signal with nothing to show: nothing in its source today (or no detector),
 * no candidate ever, no lead in lemlist and no activity. Collapsed into one
 * line under the table (S01, S05, S07 in Oct 2026). Exported for tests.
 */
export function isEmptySignal(row: SignalOverviewRow): boolean {
  return isDormantSignal(row) && !row.detected && CANDIDATE_STATUSES.every((s) => (row.candidates[s] ?? 0) === 0);
}

/** Whether any signal has a lemlist number in the period, i.e. whether the lemlist columns are drawn at all. */
export function anyLemlistColumn(rows: SignalOverviewRow[]): boolean {
  return (['inLemlist', 'contacted', 'connected', 'replied', 'lastActivity'] as const).some(
    (c) => !allZero(rows, COLUMN_PICK[c]),
  );
}

/** Candidates proposed or confirmed but not yet in lemlist. */
function waitingCandidates(rows: SignalOverviewRow[]): number {
  return rows.reduce((n, s) => n + s.candidates.proposed + s.candidates.confirmed, 0);
}

/** "8 signal campaigns paused" / "8 signal campaigns (6 paused, 2 without a campaign)". */
function dormantSummary(rows: SignalOverviewRow[]): string {
  const counts = statusCounts(rows);
  const noun = `signal campaign${rows.length === 1 ? '' : 's'}`;
  if (counts.length === 1) return `${rows.length} ${noun} ${counts[0]![0]}`;
  return `${rows.length} ${noun} (${counts.map(([status, n]) => `${n} ${status}`).join(', ')})`;
}

/** The optional columns of the signals table: each is hidden when every visible row is 0 or empty. */
type SignalColumn = 'sourced' | 'inLemlist' | 'contacted' | 'connected' | 'replied' | 'lastActivity';

const COLUMN_PICK: Record<SignalColumn, (r: SignalOverviewRow) => unknown> = {
  sourced: (r) => r.detected ?? 0,
  inLemlist: (r) => r.leadsInCampaign,
  contacted: (r) => r.funnel.contacted,
  connected: (r) => r.funnel.connected,
  replied: (r) => r.funnel.replied,
  lastActivity: (r) => r.funnel.lastActivityAt ?? 0,
};

/** All-zero rule: a column whose value is 0 or empty on every row is not drawn. */
function allZero(rows: SignalOverviewRow[], pick: (r: SignalOverviewRow) => unknown): boolean {
  if (rows.length === 0) return true;
  if (rows.length === 1) return !pick(rows[0]!);
  return isConstant(rows, pick) && !pick(rows[0]!);
}

const COLUMN_WIDTH: Record<'signal' | 'campaign' | 'candidates' | SignalColumn, string> = {
  signal: '15rem',
  campaign: 'auto',
  candidates: '16rem',
  sourced: '7rem',
  inLemlist: '6rem',
  contacted: '6.5rem',
  connected: '6.5rem',
  replied: '7rem',
  lastActivity: '8.5rem',
};

function SignalRow({
  row,
  cols,
  candidatesLast,
  showStatus,
  stripSuffix,
  showCampaign,
  onOpen,
}: {
  row: SignalOverviewRow;
  cols: Set<SignalColumn>;
  candidatesLast: boolean;
  showStatus: boolean;
  stripSuffix: boolean;
  showCampaign: boolean;
  onOpen: (playId: string) => void;
}) {
  return (
    <Tr onClick={() => onOpen(row.playId)}>
      <Td className="font-medium text-text-primary">
        {signalTabLabel(row.campaignPrefix, row.label)}
        {/* The lemlist campaign sits under the signal only when it says more (a different name, a status, a problem). */}
        {!showCampaign && (
          <div className="font-normal">
            <SignalCampaignCell row={row} showStatus={showStatus} stripSuffix={stripSuffix} />
          </div>
        )}
        {/* Its own line: inline after a long label it read as one word ("…in seatno detector"). */}
        {row.detectorGap && (
          <span className="block text-body-xs font-normal text-warning-dark" title={row.detectorGap}>
            No detector
          </span>
        )}
      </Td>
      {showCampaign && <Td><SignalCampaignCell row={row} showStatus={showStatus} stripSuffix={stripSuffix} /></Td>}
      {cols.has('sourced') && (
        <Td align="right">
          {row.detected === null ? (
            <span className="cursor-help" title={row.detectorGap ?? 'No detector for this play'}><Nil /></span>
          ) : (
            <Num value={row.detected} />
          )}
        </Td>
      )}
      <Td className={clsx(cols.has('sourced') && '!pl-6', candidatesLast && '!pr-3')}><CandidateChips counts={row.candidates} /></Td>
      {cols.has('inLemlist') && <Td align="right"><Num value={row.leadsInCampaign} /></Td>}
      {cols.has('contacted') && <Td align="right"><Num value={row.funnel.contacted} strong /></Td>}
      {cols.has('connected') && (
        <Td align="right">
          <Rate value={row.funnel.connectRate} n={row.funnel.connected} total={row.funnel.invited} unit="invited" />
        </Td>
      )}
      {cols.has('replied') && (
        <Td align="right">
          <span className="tabular-nums whitespace-nowrap">
            <span className="block"><Num value={row.funnel.replied} /></span>
            {row.funnel.contacted > 0 && (
              <span className="block text-body-xs text-text-tertiary">
                {formatRate(row.funnel.replyRate)}
                {/* Interested is too rare for a column of its own: it rides on the reply cell when there is one. */}
                {row.funnel.interested > 0 && ` · ${row.funnel.interested} marked interested`}
              </span>
            )}
          </span>
        </Td>
      )}
      {cols.has('lastActivity') && (
        <Td align="right" className="whitespace-nowrap text-text-tertiary !pr-3">
          {row.funnel.lastActivityAt ? formatDateTime(row.funnel.lastActivityAt) : <Nil />}
        </Td>
      )}
    </Tr>
  );
}

function SignalsTable({
  rows,
  dormant,
  showDormant,
  onToggleDormant,
  onOpenSignal,
  dormantStatusInHeader = false,
  allPausedInBanner = false,
}: {
  rows: SignalOverviewRow[];
  dormant: SignalOverviewRow[];
  showDormant: boolean;
  onToggleDormant?: () => void;
  onOpenSignal: (playId: string) => void;
  /** The rows are all dormant and the panel header already names their status ("8 signal campaigns paused"). */
  dormantStatusInHeader?: boolean;
  /** Every signal campaign is paused and the banner says so once: no Paused pill on any row. */
  allPausedInBanner?: boolean;
}) {
  const dormantProposed = dormant.reduce((n, s) => n + s.candidates.proposed, 0);
  const visible = showDormant ? [...rows, ...dormant] : rows;
  const cols = new Set(
    (Object.keys(COLUMN_PICK) as SignalColumn[]).filter((c) => !allZero(visible, COLUMN_PICK[c])),
  );
  // The status the summary line names for the dormant rows (the most common one)
  // is said there once; a row only gets a status pill when it is the exception.
  const dormantMajority = statusCounts(dormantStatusInHeader ? rows : dormant)[0]?.[0] ?? null;
  const isDormantRow = (row: SignalOverviewRow) => dormantStatusInHeader || dormant.includes(row);
  const showStatus = (row: SignalOverviewRow) =>
    !(allPausedInBanner && statusOf(row).toLowerCase() === 'paused') && !(isDormantRow(row) && statusOf(row) === dormantMajority);
  const stripSuffix = allLinkedIn(visible.flatMap((r) => (r.campaign ? [r.campaign.name] : [])));
  // The campaign goes under each signal's name rather than in a column of its own: a
  // column read "same name" on most rows (review #10). campaignColumnNeeded stays for tests.
  const showCampaign = false;

  const order: Array<'signal' | 'campaign' | 'candidates' | SignalColumn> = [
    'signal',
    ...(showCampaign ? (['campaign'] as const) : []),
    ...(cols.has('sourced') ? (['sourced'] as const) : []),
    'candidates',
    ...(['inLemlist', 'contacted', 'connected', 'replied', 'lastActivity'] as const).filter((c) => cols.has(c)),
  ];
  // Without the Campaign column the signal name takes the free width.
  const colWidths = order.map((c) => (c === 'signal' && !showCampaign ? 'auto' : COLUMN_WIDTH[c]));
  const colCount = order.length;
  const lastIsActivity = cols.has('lastActivity');
  const candidatesLast = order[order.length - 1] === 'candidates';

  return (
    <Table colWidths={colWidths} minWidth={colCount > 6 ? 1000 : undefined}>
      <THead>
        <tr>
          <Th>Signal</Th>
          {showCampaign && <Th title="The lemlist campaign, where its name says more than the signal's">Campaign</Th>}
          {cols.has('sourced') && (
            <Th align="right" title="Companies (or people) the source holds today, before the play's trade, panel-size, customer and live-campaign filters. Candidates are all-time, so they can be more than this.">In source now</Th>
          )}
          {/* Padded off In source now: a right-aligned header next to a left-aligned one ran together. */}
          <Th className={clsx(cols.has('sourced') && '!pl-6', candidatesLast && '!pr-3')} title="Every candidate written since the play started, by status">Candidates, all time</Th>
          {cols.has('inLemlist') && <Th align="right">In lemlist</Th>}
          {cols.has('contacted') && <Th align="right">Contacted</Th>}
          {cols.has('connected') && <Th align="right" title={CONNECTED_TOOLTIP}>Connected</Th>}
          {cols.has('replied') && <Th align="right">Replied · %</Th>}
          {lastIsActivity && <Th align="right" className="!pr-3" title={LAST_ACTIVITY_TOOLTIP}>Last activity</Th>}
        </tr>
      </THead>
      <tbody>
        {rows.map((row) => (
          <SignalRow key={row.playId} row={row} cols={cols} candidatesLast={candidatesLast} showStatus={showStatus(row)} stripSuffix={stripSuffix} showCampaign={showCampaign} onOpen={onOpenSignal} />
        ))}
        {onToggleDormant && dormant.length > 0 && (
          <tr className="border-b border-border-light bg-surface-secondary">
            <td colSpan={colCount} className="py-2 px-2 text-body-sm text-text-secondary">
              <button
                type="button"
                onClick={onToggleDormant}
                className="inline-flex items-center gap-1 hover:text-text-primary"
                title="Plays that have not enrolled anyone and have no lemlist activity in the period"
              >
                <ChevronRight className={clsx('h-4 w-4 transition-transform', showDormant && 'rotate-90')} />
                {dormantSummary(dormant)} · 0 enrolled · {plural(dormantProposed, 'candidate')} proposed
              </button>
            </td>
          </tr>
        )}
        {showDormant &&
          dormant.map((row) => (
            <SignalRow key={row.playId} row={row} cols={cols} candidatesLast={candidatesLast} showStatus={showStatus(row)} stripSuffix={stripSuffix} showCampaign={showCampaign} onOpen={onOpenSignal} />
          ))}
      </tbody>
    </Table>
  );
}

/**
 * Outbound → signals: one row per signal play, shown expanded. When plays sit
 * idle, one exception line on top says how many and how many candidates wait
 * ("8 signal campaigns paused · 1,523 candidates waiting").
 */
export function SignalsPanel({
  data,
  onOpenSignal,
}: {
  data: OutreachOverview;
  onOpenSignal: (playId: string) => void;
}) {
  const [showDormant, setShowDormant] = useState(true);
  const [showEmpty, setShowEmpty] = useState(false);
  // Signals with nothing at all are one line under the table, unless that would leave no table.
  const emptyAll = data.signals.filter(isEmptySignal);
  const empty = emptyAll.length < data.signals.length ? emptyAll : [];
  const shown = data.signals.filter((s) => !empty.includes(s));
  const live = shown.filter((s) => !isDormantSignal(s));
  const dormant = shown.filter((s) => isDormantSignal(s));

  if (data.signals.length === 0) return null;

  const waiting = waitingCandidates(data.signals);
  const linkedIn = allLinkedIn(data.signals.flatMap((r) => (r.campaign ? [r.campaign.name] : [])));
  // Every play paused: said once, for all of them (the 3 with nothing yet included), and no row repeats it.
  const allPaused = allSignalsPaused(data.signals);
  const exception = allPaused
    ? `All ${data.signals.length} signal campaigns paused in lemlist · ${plural(waiting, 'candidate')} waiting`
    : dormant.length > 0
      ? `${dormantSummary(dormant)} · ${plural(waiting, 'candidate')} waiting`
      : null;
  const lemlistCols = anyLemlistColumn(showEmpty ? data.signals : shown);
  const description = [
    "Candidates are all-time: proposed (found by the play, not yet approved), confirmed, enrolled in lemlist, blocked or dismissed (turned down by hand); In source now is today's count, so the two don't add up.",
    lemlistCols
      ? 'The lemlist columns (In lemlist to Last activity) are for the selected period.'
      : 'No signal campaign has a lead or any lemlist activity in the selected period, so the lemlist columns are left out.',
    linkedIn ? 'All signal campaigns are LinkedIn campaigns.' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Card padding="md">
      <CardHeader title="Signals" description={description} />
      {exception && (
        // An exception, not context: idle plays with people waiting is a warning, so it is amber.
        <p className="mb-3 flex items-center gap-2 rounded-lg border border-warning/20 bg-warning-light px-3 py-2 text-body-sm font-medium text-warning-dark">
          <AlertTriangle className="h-4 w-4 flex-none text-warning" aria-hidden />
          {exception}
          {/* Where to act: campaigns are resumed in lemlist, not here. Open a signal for its own campaign link. */}
          <a
            href="https://app.lemlist.com/campaigns"
            target="_blank"
            rel="noreferrer"
            className="ml-auto whitespace-nowrap font-normal text-accent hover:underline"
          >
            Resume in lemlist ↗
          </a>
        </p>
      )}
      {live.length === 0 ? (
        <SignalsTable
          rows={showEmpty ? [...dormant, ...empty] : dormant}
          dormant={[]}
          showDormant={false}
          onOpenSignal={onOpenSignal}
          dormantStatusInHeader
          allPausedInBanner={allPaused}
        />
      ) : (
        <SignalsTable
          rows={live}
          dormant={showEmpty ? [...dormant, ...empty] : dormant}
          showDormant={showDormant}
          onToggleDormant={() => setShowDormant(!showDormant)}
          onOpenSignal={onOpenSignal}
          allPausedInBanner={allPaused}
        />
      )}
      {empty.length > 0 && (
        <button
          type="button"
          onClick={() => setShowEmpty(!showEmpty)}
          className="mt-2 inline-flex items-center gap-1 text-body-xs text-text-tertiary hover:text-text-primary"
          title="Nothing in the source today (or no detector), no candidate ever, no lead in lemlist and no activity"
        >
          <ChevronRight className={clsx('h-3.5 w-3.5 transition-transform', showEmpty && 'rotate-90')} />
          {showEmpty ? 'Hide' : 'Show'} {plural(empty.length, 'signal')} with nothing yet:{' '}
          {empty.map((r) => signalTabLabel(r.campaignPrefix, r.label)).join(', ')}
        </button>
      )}
    </Card>
  );
}
